import type { Transformation } from 'cloudinary-core';
import { Media, ProfileMedia, AuditionGroupMedia } from '@minecraft.graphql-operations';
import { gqlMedia, gqlProfileMedia, gqlAuditionGroupMedia, gqlMediaStorageStatusEnum } from '@minecraft.graphql-types';
import { constants, DeepPartial } from '@minecraft.utils';
import type { MediaPhotoProps, MediaPhotoSource } from '../types';
import {
  getCloudinary,
  getMediaDownloadOriginalUrl,
  getMediaNameWithExtension,
  isMediaInColdStorage,
  sanitizeDownloadFileName,
  isMediaLocked,
} from './mediaHookUtils';

type FormatMediaToPhotoArgs<T> = {
  media: T;
};

type PhotoMedia = DeepPartial<gqlMedia> | DeepPartial<Media>;

type BuildPhotoSrcArgs<T extends PhotoMedia> = {
  cld: ReturnType<typeof getCloudinary>;
  media: T;
  fileKey: string;
  width: number;
  height: number;
};

type BuildThumbnailSrcArgs<T extends PhotoMedia> = {
  cld: ReturnType<typeof getCloudinary>;
  media: T;
  fileKey: string;
  width: number;
  height: number;
};

const DEFAULT_PHOTO_FILE_KEY = 'defaults/profile-placeholder_de6qma.png';
const { commonPhotoTransformationParams, migratedPhotoTransformationParams } = constants;

const PHOTO_SIZES: Array<[number, number]> = [
  [600, 600],
  [800, 800],
];

const THUMB_SIZES: Array<[number, number]> = [
  [192, 240],
  [240, 300],
];

/**
 * Builds the source URL for a photo media item.
 * This is not finalized and will change when we do normal "photo" migration
 */
const buildPhotoMediaSrc = <T extends PhotoMedia>({
  cld,
  media,
  fileKey,
  width,
  height,
}: BuildPhotoSrcArgs<T>): MediaPhotoSource => {
  const mediaTransformation = media?.transformation;
  const mediaMigrated = isMediaInColdStorage(media?.mediaStorageStatus?.code);
  const transformations: Transformation.Options[] = [];

  if (mediaMigrated) {
    transformations.push({ angle: migratedPhotoTransformationParams.a });
  }

  if (mediaTransformation) {
    transformations.push({
      x: mediaTransformation.xAxis,
      y: mediaTransformation.yAxis,
      width: mediaTransformation.width,
      height: mediaTransformation.height,
      angle: mediaTransformation.rotate,
      crop: 'crop',
    });
  }

  transformations.push({
    width,
    height,
    crop: 'fit',
  });

  return {
    src: cld.url(fileKey || DEFAULT_PHOTO_FILE_KEY, {
      transformation: transformations,
      background: 'transparent',
      fetchFormat: commonPhotoTransformationParams.f,
      quality: commonPhotoTransformationParams.q,
    }),
    width,
    height,
  };
};

const buildThumbMediaSrc = <T extends PhotoMedia>({
  cld,
  media,
  fileKey,
  width,
  height,
}: BuildThumbnailSrcArgs<T>): MediaPhotoSource => {
  const mediaMigrated = isMediaInColdStorage(media?.mediaStorageStatus?.code);
  const thumbnail = media?.thumbnail;
  const thumbnailTransformation = thumbnail?.transformation;
  const thumbnailFileKey =
    mediaMigrated && thumbnail?.fileKey && !thumbnailTransformation ? thumbnail.fileKey : fileKey;
  const transformations: Transformation.Options[] = [];

  if (mediaMigrated) {
    transformations.push({ angle: migratedPhotoTransformationParams.a });
  }

  if (thumbnailTransformation) {
    transformations.push({
      x: thumbnailTransformation.xAxis,
      y: thumbnailTransformation.yAxis,
      width: thumbnailTransformation.width,
      height: thumbnailTransformation.height,
      angle: thumbnailTransformation.rotate,
      crop: 'crop',
    });
  }

  // thumbnails in cloudinary have facial gravity and a slight zoom out
  // crop: thumb works with the facial gravity and zoom settings
  transformations.push({
    width,
    height,
    crop: 'thumb',
    gravity: 'face',
    zoom: '0.7',
  });

  return {
    src: cld.url(thumbnailFileKey || DEFAULT_PHOTO_FILE_KEY, {
      transformation: transformations,
      background: 'transparent',
      fetchFormat: commonPhotoTransformationParams.f,
      quality: commonPhotoTransformationParams.q,
    }),
    width,
    height,
  };
};

export const formatMediaToPhoto = <T extends DeepPartial<gqlMedia> | DeepPartial<Media>>({
  media,
}: FormatMediaToPhotoArgs<T>): MediaPhotoProps => {
  // all photos resolve against the hot storage cloud, even for migrated media
  const cld = getCloudinary({ code: gqlMediaStorageStatusEnum.UPLOADED });
  const fileKey = media?.fileKey;
  const mediaName = media?.name;
  const mediaNameWithExt = getMediaNameWithExtension({ media });

  const photoSrcs = PHOTO_SIZES.map(([width, height]) =>
    buildPhotoMediaSrc({
      cld,
      media,
      fileKey,
      width,
      height,
    })
  );
  const thumbnailSrcs = THUMB_SIZES.map(([width, height]) =>
    buildThumbMediaSrc({ cld, media, fileKey, width, height })
  );

  const downloadSrc = cld.url(fileKey, {
    // eslint-disable-next-line camelcase
    force_version: false,
    flags: `attachment:${sanitizeDownloadFileName(mediaName)}`,
  });

  return {
    photoSrcs,
    thumbnailSrcs,
    downloadUrl: getMediaDownloadOriginalUrl({ primaryUrl: downloadSrc }),
    name: mediaName,
    nameWithExt: mediaNameWithExt,
    isLocked: isMediaLocked({ media }),
  };
};

export const formatProfileMediaToPhoto = <T extends DeepPartial<gqlProfileMedia> | DeepPartial<ProfileMedia>>({
  profileMedia,
}: {
  profileMedia: T;
}): MediaPhotoProps => {
  return formatMediaToPhoto({ media: profileMedia?.media });
};

export const formatAuditionGroupMediaToPhoto = <
  T extends DeepPartial<gqlAuditionGroupMedia> | DeepPartial<AuditionGroupMedia>
>({
  groupMedia,
}: {
  /** the audition group media object from the network query */
  groupMedia: T;
}): MediaPhotoProps => {
  const media = groupMedia?.media;
  const name = groupMedia?.name || media?.name;
  return formatMediaToPhoto({ media: { ...media, name } });
};
